import { memo } from "react";
import { ServiceTitanInvoiceLink } from "@/core/components/service-titan-invoice-link";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityPricebookItem } from "@/activity-feed/query";
import { formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { DetailCard, InfoPair } from "./common";

interface PricebookItemDetailCardProps {
  invoiceItemId: string;
  inView: boolean;
}

export const PricebookItemDetailCard = memo(
  ({ invoiceItemId, inView }: PricebookItemDetailCardProps) => {
    const { data: pricebookItem, isLoading } = useGetActivityPricebookItem(
      invoiceItemId,
      inView,
    );

    // Show loading only if we don't have data yet
    if (!pricebookItem && (!inView || isLoading)) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.pricebook_item_sale}>
          <Stack gap="sm" fullWidth>
            <Skeleton className="h-5 w-56" />
            <div className="flex flex-wrap gap-x-10 gap-y-2">
              <Skeleton className="h-4 w-52" />
              <Skeleton className="h-4 w-40" />
            </div>
          </Stack>
        </DetailCard>
      );
    }

    if (!pricebookItem) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.pricebook_item_sale}>
          <Stack gap="sm" fullWidth>
            <Typography variant="subtitleW2">Pricebook Item Details</Typography>
            <Typography variant="bodyW3" textColor="secondary">
              Pricebook item information unavailable
            </Typography>
          </Stack>
        </DetailCard>
      );
    }

    return (
      <DetailCard eventType={SPIFF_RULE_EVENTS.pricebook_item_sale}>
        <Stack gap="sm" fullWidth>
          <Typography variant="subtitleW2">
            {pricebookItem.display_name}
          </Typography>
          {pricebookItem.service_titan_invoice?.external_id && (
            <Stack direction="row" gap="xs">
              <Typography>Associated Invoice:</Typography>
              <ServiceTitanInvoiceLink
                invoiceId={pricebookItem.service_titan_invoice.external_id}
              />
            </Stack>
          )}
          <div className="flex flex-wrap gap-x-10 gap-y-2">
            {pricebookItem.service_titan_job?.completed_at && (
              <InfoPair
                label="Job Completion"
                value={formatDate(pricebookItem.service_titan_job.completed_at)}
              />
            )}
            <InfoPair
              label="Invoice Line Item"
              value={formatMoneyCents(pricebookItem.total_cents)}
            />
          </div>
        </Stack>
      </DetailCard>
    );
  },
);

PricebookItemDetailCard.displayName = "PricebookItemDetailCard";
